import type { EntitlementItem } from "~/features/entitlements/types"
import { keysFor } from "~/features/entitlements/windows"
import { EntitlementCategory } from "~/graphql/generated/graphql"
import { ebookHref } from "./projections"
import type { LibraryCardItem } from "./types"

/**
 * Commerce seats that `libraryV2` cannot serve, rendered into the library
 * section they belong to — as dead cards where there is nothing to open, and as
 * real reader links where the seat itself carries enough to open one.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 *
 * An expired grant has no library row to gray out. `AccessRevoked` deletes the
 * row outright — `media-backend-mono-repo`
 * `library/.../CustomerAccessUpdatedHandler.scala:147` →
 * `handleAccessRevoked(accessRepo.delete, …)`, keyed on `(lin, consumerId)`,
 * with no tombstone, no flag and no end date retained — and the row carries no
 * date to compare against anyway (FINDINGS.md gaps 23 and 30).
 *
 * Observed on the UAT account: both `2026 Feast Virtual Event` seats lapsed on
 * `2026-05-01` and are absent from the `DIGITAL_EVENT` connection, while
 * `/seats` still carries them with their dates. So the seat is the ONLY record
 * of them, and the Digital Events section can show them only by taking them
 * from the commerce half. That is what this does.
 *
 * Two rules keep it from inventing content:
 *
 * 1. **Orphans only.** A seat whose title matches a card `libraryV2` already
 *    returned is skipped — that card is the better record, and it gets the seat's
 *    window annotated onto it through `AccessWindowContext` instead.
 * 2. **Lapsed always; live only when we can link out of the app.** A plainly
 *    active seat missing from `libraryV2` is a different bug (gaps 9 and 22 —
 *    never granted on an allow-listed channel), but withholding it means a
 *    customer sees nothing at all for something they own.
 *
 *    What decides it is where the card would go. A synthesised card that links
 *    INWARD — `/study/{id}`, `/event/{id}` — promises a detail screen backed by
 *    an asset the graph did not give us, so it stays withheld. A card that links
 *    OUTWARD carries the whole promise in the URL and needs no asset behind it.
 *
 *    Ebooks hand Lifeway Reader the seat's `itemNumber` and nothing else.
 *    Digital Events hand over whatever `accessTarget.ts` derived from the seat's
 *    distribution channel — `live.lifeway.com` for `LLC`. Both were observed on
 *    UAT as live seats absent from `libraryV2`: the bible study ebooks, and
 *    `Kingdom Men Rising - Digital Pass`. Before this they appeared in NO section
 *    of the page, because `sections.ts` drops those categories on the grounds
 *    that these sections render them, and this one rendered only the lapsed.
 *
 *    Studies is deliberately not in that list. Its cards open in-app, and a
 *    player link beside them would behave unlike every other card in the section.
 *
 *    The two differ in one detail. Ebooks render every live seat, including one
 *    with nothing to link with, because this section has carried them since it
 *    was their only home. Events render a live seat only when a channel rule
 *    resolved a destination, since an events card that opens nothing is new
 *    clutter rather than a recovered item.
 *
 * A lapsed card never links, in any section: access has ended, so there is
 * nothing to open, and MLC renders the same rows as a disabled button with an
 * `Expired` pill (`utils/tableCellHelpers.js#resourceLink`).
 */

/** Commerce's taxonomy against this page's sections. Mirrors `ALREADY_SERVED` in `entitlements/sections.ts`. */
const SECTION_BY_CATEGORY: Partial<Record<EntitlementCategory, string>> = {
  [EntitlementCategory.Simulcast]: "events",
  [EntitlementCategory.Video]: "studies",
  [EntitlementCategory.Ebook]: "ebooks",
}

/** The section whose synthesised cards are built from an identifier rather than an asset. */
const EBOOKS_SLUG = "ebooks"
const EVENTS_SLUG = "events"

/** Access that has ended or has not started — the states with no live asset behind them. */
const isLapsed = (item: EntitlementItem): boolean => item.window.state === "expired" || item.window.state === "upcoming"

/** No asset to open — the pill carries the reason. */
const DEAD_CARD = { href: undefined, isExternal: false } as const

type CardLink = Pick<LibraryCardItem, "href" | "isExternal" | "linkIdKind">

/**
 * Where a live seat's card would send someone, or nothing when this build cannot
 * say — in which case the seat is withheld rather than rendered as a card that
 * goes nowhere.
 *
 * The events branch requires `accessLabel`, not just `accessHref`. `accessHref`
 * always has a value: it falls back to myLifeway's `/redirect`, which resolves
 * the destination client-side with the signed-in user's token and therefore
 * lands on sign-in in a cold tab (see `accessTarget.ts`). `accessLabel` is set
 * only when a channel rule actually matched, so it is the honest signal that the
 * href was derived rather than fallen back to.
 */
const externalLinkFor = (slug: string, item: EntitlementItem): CardLink => {
  if (item.window.state !== "active") {
    return DEAD_CARD
  }

  if (slug === EBOOKS_SLUG) {
    /**
     * An ebook seat with no item number is unobserved (FINDINGS gap 21 has
     * `itemNumber` on every row) but is the only thing left to link with once
     * the aggregate id is gone.
     */
    const link = ebookHref({ id: undefined, lin: item.itemNumber || undefined })

    return link.href ? { ...link, isExternal: true } : DEAD_CARD
  }

  if (slug === EVENTS_SLUG && item.accessLabel && item.accessHref) {
    return { href: item.accessHref, isExternal: true }
  }

  return DEAD_CARD
}

const toOrphanCard = (item: EntitlementItem, link: CardLink): LibraryCardItem => ({
  id: `entitlement:${item.id}`,
  title: item.title,
  audience: item.audience,
  meta: item.meta,
  imageUrl: item.imageUrl,
  accessWindow: item.window,
  ...link,
})

/**
 * The seats belonging to `slug` that no loaded card already covers — lapsed
 * everywhere, and live ones wherever this build can link out of the app (rule 2).
 *
 * `loadedTitles` is the section's own cards, matched on the same normalised keys
 * `windows.ts` uses, so a seat and its library card cannot both render.
 */
export const orphanCardsFor = (
  slug: string,
  items: readonly EntitlementItem[],
  loadedTitles: readonly string[],
): readonly LibraryCardItem[] => {
  const covered = new Set(loadedTitles.flatMap(title => keysFor(title)))

  return (
    items
      .filter(item => SECTION_BY_CATEGORY[item.category] === slug)
      .filter(item => !keysFor(item.title).some(key => covered.has(key)))
      .map(item => ({ item, link: externalLinkFor(slug, item) }))
      /**
       * Ebooks keep rendering every live seat, linkable or not: this section has
       * shown them since they were the only way a customer saw a bible study ebook
       * at all, and a seat with no `itemNumber` to link with (unobserved — gap 21)
       * is still worth showing as a dead card. Events are the narrower case and
       * appear only when there is somewhere to send someone.
       */
      .filter(({ item, link }) => isLapsed(item) || slug === EBOOKS_SLUG || link.href !== undefined)
      .map(({ item, link }) => toOrphanCard(item, link))
  )
}
